import Vue from "vue";
import forEach from "lodash/forEach";
import isFunction from "lodash/isFunction";
import swal from "sweetalert2";
import confetti from "canvas-confetti";

import notificationImage from 'variantImages/notification.png';
import AttemptsIndicator from "src/components/common/AttemptsIndicator";
import router from "src/router/router";
import apiService from "src/services/api/apiService";
import store from "src/store/store";
import userMessages from "src/services/userMessages";
import log from "src/services/logger";
const logTag = "notificationService";

// swal's own default is 20 - the akademia2 knowledge-test modals are redesigned to 32. It has to be
// passed as an option, as swal v6 writes it to modal.style.padding and that beats any stylesheet rule.
const knowledgeTestModalPadding = VARIANT === 'akademia2' ? 32 : 20;

// holding the notification polling interval handle so that we can clear it etc.
let notificationPollingInProgress = false;
let notificationTimeoutId;

// holding info for custom modal queue as swal is not great in this
let modalQueue = [];
let modalIsVisible = false;

// try to access the Notification API and get the necessary permission to show desktop-like notifications
const acquirePermission = function() {
    if (typeof Notification !== "undefined") {
        return requestPermission();

    // in IE, 'Notification' object is undefined at first - probably while the system plugin is being
    // loaded. We need to wait for it
    } else {
        log.log(logTag, "'Notification' is undefined, waiting");
        return new Promise((resolve) => setTimeout(resolve, 5000)).then(acquirePermission);
    }
};

const requestPermission = function() {
    return new Promise((resolve, reject) => {

        if (Notification.permission === "granted") {
            log.log(logTag, "Notification permission already granted, doing nothing");
            resolve(true);

        } else {
            log.log(logTag, "Requesting Notification permission");
            Notification.requestPermission((response) => {
                if (response === "granted") {
                    log.log(logTag, "Notification permission granted!");
                    resolve(true);
                } else {
                    log.log(logTag, "Notification permission blocked!");
                    reject(false);
                }
            });
        }
    });
};

// desktop notifications - Notification API
const notify = function(
    { title = "", body = "" },
    onClickCallback,
    timeout = config.notifications.standardNotificationDuration
) {

    acquirePermission()

    // show the actual notification
    .then(() => {
        log.log(logTag, "Showing a notification. Title: " + title + ", body: " + body + ", time: " + timeout);

        // @note we're using one notificationImage for all the notification types and areas.
        // If we want customizable images for areas, we need to get the link from server
        let n = new Notification(title, {
            body : body,
            icon : notificationImage,
            requireInteraction : timeout === 0
        });

        // we need to hide the notification manually as not every browser does this
        if (timeout !== 0) {
            setTimeout(function() {
                // there should be "n.close.bind(n)" according to MDN but it doesn"t work
                // in IE11 (says bind is undefined)
                n.close();
            }, timeout);
        }

        // call a callback on click
        n.onclick = () => { onClickCallback(n); };
    })

    // we can't do anything, this means the user has blocked the permission. He needs to allow it manually
    .catch(() => console.error("Please check the permissions for Notifications and allow them."));
};

// polling server for new notifications
const startNotificationPolling = function() {
    // if we are already polling for notifications, do nothing
    if (notificationPollingInProgress) {
        log.log(logTag, "Notifications polling already started!");
        return;
    }
    notificationPollingInProgress = true;

    log.log(logTag, "Starting notifications polling.");
    getNotificationsPeriodically();
};

// stop the last timeout + stop the whole polling
const stopNotificationPolling = function() {
    if (notificationPollingInProgress) {
        log.log(logTag, "Stopping notifications polling, last timeout ID: " + notificationTimeoutId);
        // stop the last timeout
        clearTimeout(notificationTimeoutId);
        notificationTimeoutId = undefined;

        notificationPollingInProgress = false;
    }
};

// instead of setInterval, we use "recursion" - the function sets timeout when it finishes. This prevents
// the polls to stack in case of long server responses etc
const getNotificationsPeriodically = function() {
    getAndProcessNotifications()
    .then(() => {
        if (notificationPollingInProgress) {
            notificationTimeoutId = setTimeout(
                getNotificationsPeriodically, config.notifications.notificationPollingInterval
            );
        }
    });
};

const getAndProcessNotifications = function() {
    log.log(logTag, "Getting and processing notifications...", true);
    return apiService.getNotifications().then(result => {
        // @note: we take only first three notifications (browser won't show more anyway). They are
        // sorted by their "time sent" by server. Then we reverse them so that the newest one is on top
        // of the three shown notifications
        let slicedNotifications = result.data.slice(0, 3).reverse();

        forEach(slicedNotifications, function(dataToShow) {
            // determine where to redirect after the user clicks on notification or whether to
            // show a modal
            switch (dataToShow.type) {
                // #### web notifications ####
                case "items" :
                    notify(dataToShow, (notification) => {
                        log.log(logTag, "Item notification clicked!");
                        window.focus();         // does not work in IE
                        notification.close();   // in IE, this is done automatically anyway
                        router.push("/item-dashboard");
                    }, 0);
                    break;

                case "message" :
                default :
                    notify(dataToShow, (notification) => {
                        log.log(logTag, "Message notification clicked!");
                        notification.close();
                    }, 0);
                    break;

                // #### modals ####
                case "challengedForBattle" :
                    store.dispatch("downloadBattles");
                    battleUserChallenged(
                        dataToShow.opponentName, dataToShow.declinePoints, store.getters.currentAreaCssClass,
                        (modalResult) => {
                            if (modalResult === true) {
                                store.dispatch("acceptBattle").then(response => {
                                    store.dispatch("showSuccessMessage", userMessages.afterBattleAccept(response));
                                })
                                .catch(reason => {
                                    store.dispatch("showErrorMessage", userMessages.afterBattleAccept(reason));
                                });
                                router.push("/battles").catch(() => {});

                            } else if (modalResult === "cancel") {
                                store.dispatch("declineBattle").then(response => {
                                    store.dispatch("showSuccessMessage", userMessages.afterBattleDecline(response));
                                })
                                .catch(reason => {
                                    store.dispatch("showErrorMessage", userMessages.afterBattleDecline(reason));
                                });
                            }
                            // a click outside the modal just closes it, no decline
                        }
                    );
                    break;

                // the opponent has accepted user's challenge
                case "battleAccepted" :
                    store.dispatch("downloadBattles");
                    battleOpponentAccepted(
                        store.getters.user.name, dataToShow.opponentName, store.getters.currentAreaCssClass,
                        () => { router.push("/battles").catch(() => {}); }
                    );
                    break;

                // the opponent has declined user's challenge
                case "battleDeclined" :
                    store.dispatch("downloadBattles");
                    battleOpponentDeclined(
                        store.getters.user.name, dataToShow.opponentName, store.getters.currentAreaCssClass
                    );
                    break;

                case "battleWon" :
                    store.dispatch("downloadBattles");
                    store.dispatch("downloadUserData");
                    battleWon(
                        store.getters.user.name, dataToShow.opponentName, dataToShow.points,
                        store.getters.currentAreaCssClass
                    );
                    break;

                case "battleLost" :
                    store.dispatch("downloadBattles");
                    store.dispatch("downloadUserData");
                    battleLost(
                        store.getters.user.name, dataToShow.opponentName, dataToShow.points,
                        store.getters.currentAreaCssClass
                    );
                    break;

                case "battleTie" :
                    store.dispatch("downloadBattles");
                    store.dispatch("downloadUserData");
                    battleTie(
                        store.getters.user.name, dataToShow.opponentName, dataToShow.points,
                        store.getters.currentAreaCssClass
                    );
                    break;

            }
        });
    })

    // "unauthorized" shouldn't happen here as that will log the user out
    .catch((reason) => {
        log.log(logTag, "Notification polling error!");
        log.log(logTag, reason);
    });
};

// If there is no modal in queue, show it immediately. If there is, add the next modal to the end of
// the queue
// @note: queue() returns a promise but insertQueueStep doesn't
// const showModal = function(modalObject) {
//     if (swal.getQueueStep() === null) {
//         return swal.queue([modalObject]);
//     } else {
//         return swal.insertQueueStep(modalObject);
//     }
// };

const queueModal = function(modalObject, callback) {
    modalQueue.push({ modalObject, callback });
    showNextModal();
};

const showNextModal = function() {
    // @note we can't use swal.isVisible() as that is still true in then(), unbeknowst to me why
    if (!modalIsVisible) {
        showModalWithCallback(modalQueue.shift()).then(() => {
            if (modalQueue.length > 0) {
                showNextModal();
            }
        });
    }
};

const showModalWithCallback = function({ modalObject, callback }) {
    modalIsVisible = true;

    return swal(modalObject).then(result => {
        if (isFunction(callback)) {
            callback(result);
        }
        modalIsVisible = false;
        return result;
    })

    .catch(reason => {
        if (isFunction(callback)) {
            callback(reason);
        }
        modalIsVisible = false;
        return reason;
    });
};


const newLevel = function(level, isThisHigherLevel, areaCssClass) {
    log.log(logTag, "Showing notification for new level: " + level.name + ", higher: " + isThisHigherLevel);

    let title, text = "";

    if (isThisHigherLevel) {
        title = Vue.i18n.translate("MODAL_LEVEL_UP_TITLE");
        text = Vue.i18n.translate("MODAL_LEVEL_UP_TEXT");

    } else {
        title = Vue.i18n.translate("MODAL_LEVEL_DOWN_TITLE");
        text = Vue.i18n.translate("MODAL_LEVEL_DOWN_TEXT");
    }

    let html = `<div class="inner-content">
                    <h2>${title}</h2>
                    <img src="${level.image_path}"
                         alt="Obrázek levelu" class="img img-responsive">
                    <p>${text}</p>
                    <p>
                        ${Vue.i18n.translate("MODAL_LEVEL_YOUR_NEW")}
                        <span class="level-name">${level.name}.</span>
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("MODAL_LEVEL_BUTTON"),
        confirmButtonClass  : "btn brand-secondary-btn",
        customClass         : "ifo-modal level " + " area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject);
};


const newAchievements = function(achievements, areaCssClass) {
    log.log(logTag, "Showing notification for new achievements (" + achievements.length + ")");

    let title = Vue.i18n.translate("MODAL_ACHIEVEMENT_TITLE");
    if (achievements.length > 1) {
        title = Vue.i18n.translate("MODAL_ACHIEVEMENT_TITLE_PLURAL");
    }

    let html = `<div class="inner-content">
                    <h2>${title}</h2>
                    <div class="bg-img img-responsive"></div>
                    <p>${Vue.i18n.translate("MODAL_ACHIEVEMENT_TEXT")}</p>
                        <ul class="list-group achievements-list">`;

    // ES6 string literals for teh win
    forEach(achievements, function(achievement) {
        html += `<li class="list-group-item list-item">
                    <div class="row list-item-row">
                        <div class="col-xs-3 col-sm-4 col-lg-3 img-wrapper">
                            <img src="${achievement.image_path}"
                                 class="img img-responsive" alt="Obrázek trofeje">
                        </div>
                        <div class="col-xs-9 col-sm-8 col-lg-9">
                            <div class="row">
                                <div class="col-xs-12 col-sm-7 title-wrapper">
                                    <h4>${achievement.name}</h4>
                                </div>
                                <div v-if="tag" class="col-xs-12 col-sm-5 tag">
                                    ${achievement.category_name || ''}
                                </div>
                            </div>
                            <p>${achievement.description}</p>
                        </div>
                    </div>
                </li>`;
    });

    html += `</ul></div>`;

    let swalObject = {
        // title               : null,
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("MODAL_LEVEL_BUTTON"),
        confirmButtonClass  : "btn brand-secondary-btn",
        customClass         : "ifo-modal achievements " + " area-" + areaCssClass,
    };

    // show or enqueue this modal
    queueModal(swalObject);
};

const newAchievementsSingle = function(achievements, areaCssClass) {
    log.log(logTag, "Showing single-achievement notifications (" + achievements.length + ")");

    forEach(achievements, function(achievement) {
        let html = `<div class="inner-content">
                        <div class="achievement-icon">
                            <img src="${achievement.image_path}"
                                 class="img img-responsive" alt="Obrázek trofeje">
                        </div>
                        <h3 class="achievement-name">${achievement.name}</h3>
                        <p>${achievement.description}</p>
                    </div>`;

        let swalObject = {
            html                : html,
            showCancelButton    : false,
            buttonsStyling      : false,
            confirmButtonText   : Vue.i18n.translate("MODAL_ACHIEVEMENT_BUTTON"),
            confirmButtonClass  : "btn primary-btn",
            customClass         : "ifo-modal achievements " + " area-" + areaCssClass,
            onOpen              : () => {
                const shared = {
                    particleCount   : 120,
                    spread          : 60,
                    startVelocity   : 60,
                    ticks           : 350,
                    colors          : ["#2870ED", "#21416C", "#4EBC8D", "#ffffff"],
                };
                confetti({ ...shared, angle: 60,  origin: { x: 0,   y: 0.5 } });
                confetti({ ...shared, angle: 120, origin: { x: 1,   y: 0.5 } });
            },
        };

        queueModal(swalObject);
    });
};

const newAchievementsToast = function(achievements) {
    log.log(logTag, "Showing toast notification for new achievements (" + achievements.length + ")");

    store.dispatch("showAchievementToast", achievements);
};


const battleUserChallenged = function(opponent, points, areaCssClass, callback) {
    log.log(logTag, "Showing notification for new battle challenge, area " + areaCssClass);

    let html = `<div class="inner-content">
                    <div class="bg-img img-responsive"></div>
                    <h3>
                        <span class="opponent-name">${opponent}</span>
                        ${Vue.i18n.translate("BATTLES_CHALLENGED_TITLE")}
                    </h3>
                    <p>
                        ${Vue.i18n.translate("BATTLES_CHALLENGED_TEXT")}
                        ${Vue.i18n.translate("BATTLES_CHALLENGED_POINTS", { variable : points })}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : true,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("BATTLES_ACCEPT"),
        confirmButtonClass  : "btn primary-btn",
        cancelButtonText    : Vue.i18n.translate("BATTLES_REJECT"),
        cancelButtonClass   : "btn secondary-btn",
        customClass         : "ifo-modal battles battles-challenged area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const battleOpponentAccepted = function(user, opponent, areaCssClass, callback) {
    log.log(logTag, "Showing notification for opponent accepting battle, area " + areaCssClass);

    let html = `<div class="inner-content">
                    <h3>
                        ${user} ${Vue.i18n.translate("BATTLES_VS")} 
                        <span class="opponent-name">${opponent}</span>
                    </h3>
                    <div class="bg-img img-responsive"></div>
                    <h3>
                        <span class="opponent-name">${opponent}</span>
                        ${Vue.i18n.translate("BATTLES_OPPONENT_ACCEPTED_TITLE")}
                    </h3>
                    <p>
                        ${Vue.i18n.translate("BATTLES_OPPONENT_ACCEPTED_TEXT")}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("BATTLES_OK"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal battles battles-opponent-accepted area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const battleOpponentDeclined = function(user, opponent, areaCssClass, callback) {
    log.log(logTag, "Showing notification for opponent declining battle, area " + areaCssClass);

    let html = `<div class="inner-content">
                    <h3>
                        ${user} ${Vue.i18n.translate("BATTLES_VS")} 
                        <span class="opponent-name">${opponent}</span>
                    </h3>
                    <div class="bg-img img-responsive"></div>
                    <h3>
                        ${Vue.i18n.translate("BATTLES_OPPONENT_DECLINED_TITLE")}
                    </h3>
                    <p>
                        ${Vue.i18n.translate("BATTLES_OPPONENT_DECLINED_TEXT")}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("BATTLES_OK"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal battles battles-opponent-declined area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const battleWon = function(user, opponent, points, areaCssClass, callback) {
    log.log(logTag, "Showing notification for winning battle, area " + areaCssClass);

    let html = `<div class="inner-content">
                    <h3>
                        ${user} ${Vue.i18n.translate("BATTLES_VS")} 
                        ${opponent}
                    </h3>
                    <div class="bg-img img-responsive"></div>
                    <h2>
                        ${Vue.i18n.translate("BATTLES_WON_TITLE")}
                        +${Vue.i18n.translate("POINTS_GAINED", { points })}
                    </h2>
                    <p>
                        ${Vue.i18n.translate("BATTLES_WON_TEXT")}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("BATTLES_OK"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal battles battles-won area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const battleLost = function(user, opponent, points, areaCssClass, callback) {
    log.log(logTag, "Showing notification for losing battle, area " + areaCssClass);

    let html = `<div class="inner-content">
                    <h3>
                        ${user} ${Vue.i18n.translate("BATTLES_VS")} 
                        ${opponent}
                    </h3>
                    <div class="bg-img img-responsive"></div>
                    <h2>
                        ${Vue.i18n.translate("BATTLES_LOST_TITLE")}
                        ${Vue.i18n.translate("POINTS_GAINED", { points })}
                    </h2>
                    <p>
                        ${Vue.i18n.translate("BATTLES_LOST_TEXT")}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("BATTLES_OK"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal battles battles-lost area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const battleTie = function(user, opponent, points, areaCssClass, callback) {
    log.log(logTag, "Showing notification for battle tie, area " + areaCssClass);

    let html = `<div class="inner-content">
                    <h3>
                        ${user} ${Vue.i18n.translate("BATTLES_VS")} 
                        ${opponent}
                    </h3>
                    <div class="bg-img img-responsive"></div>
                    <h2>
                        ${Vue.i18n.translate("BATTLES_TIE_TITLE")}
                        ${Vue.i18n.translate("POINTS_GAINED", { points })}
                    </h2>
                    <p>
                        ${Vue.i18n.translate("BATTLES_TIE_TEXT")}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("BATTLES_OK"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal battles battles-tie area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};


const knowledgeTestGeneric = function(
    testTitle, titleText, descriptionText, areaCssClass, callback, confirmBtnText, cancelBtnText
) {
    log.log(logTag, "Showing notification before start of Knowledge Test attempt, area " + areaCssClass);

    const confirmText = confirmBtnText || Vue.i18n.translate("KNOWLEDGE_TEST_MODAL_ENTER");
    const cancelText = cancelBtnText || Vue.i18n.translate("KNOWLEDGE_TEST_MODAL_CANCEL");

    let html = `<div class="inner-content">
                    <h4>
                        ${testTitle}
                    </h4>
                    <h3>
                       ${titleText} 
                    </h3>
                    <p>
                        ${descriptionText}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        padding             : knowledgeTestModalPadding,
        showCancelButton    : true,
        buttonsStyling      : false,
        confirmButtonText   : confirmText,
        confirmButtonClass  : "btn primary-btn",
        cancelButtonText    : cancelText,
        cancelButtonClass   : "btn secondary-btn",
        customClass         : "ifo-modal knowledge-test knowledge-test-generic area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const knowledgeTestStart = function(
    areaCssClass, neededQuestions, totalQuestions, maxAttempts, usedAttempts, callback
) {
    log.log(logTag, "Showing Knowledge Test start confirmation, area " + areaCssClass);

    const numbersKey = maxAttempts === 1
        ? "KNOWLEDGE_TEST_MODAL_NUMBERS_1"
        : maxAttempts >= 5
            ? "KNOWLEDGE_TEST_MODAL_NUMBERS_5_MORE"
            : "KNOWLEDGE_TEST_MODAL_NUMBERS";
    const numbersText = Vue.i18n.translate(numbersKey, {
        needed   : neededQuestions,
        total    : totalQuestions,
        attempts : maxAttempts
    });

    // SweetAlert2 takes an HTML string, so render the Vue component to markup once
    const indicator = new (Vue.extend(AttemptsIndicator))({
        propsData : { total : maxAttempts, used : usedAttempts }
    }).$mount();
    const attemptsHtml = indicator.$el.outerHTML;
    indicator.$destroy();

    let html = `<div class="inner-content">
                    ${attemptsHtml}
                    <div class="inner-text">
                        <h3>
                            ${Vue.i18n.translate("KNOWLEDGE_TEST_MODAL_TITLE")}
                        </h3>
                        <p>
                            ${numbersText}
                        </p>
                    </div>
                </div>`;

    let swalObject = {
        html                : html,
        padding             : knowledgeTestModalPadding,
        showCancelButton    : true,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("KNOWLEDGE_TEST_MODAL_START"),
        confirmButtonClass  : "btn primary-btn",
        cancelButtonText    : Vue.i18n.translate("KNOWLEDGE_TEST_BACK"),
        cancelButtonClass   : "btn secondary-btn",
        customClass         : "ifo-modal knowledge-test knowledge-test-start area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};

const knowledgeTestTimeout = function(testTitle, areaCssClass, callback) {
    log.log(logTag, "Showing Knowledge Test timeout notification; area " + areaCssClass);

    let html = `<div class="inner-content">
                    <h4>
                        ${testTitle}
                    </h4>
                    <h3>
                        ${Vue.i18n.translate("KNOWLEDGE_TEST_TIMEOUT_TITLE")} 
                    </h3>
                    <p>
                        ${Vue.i18n.translate("KNOWLEDGE_TEST_TIMEOUT_DESC")}
                    </p>
                </div>`;

    let swalObject = {
        html                : html,
        padding             : knowledgeTestModalPadding,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate("KNOWLEDGE_TEST_TIMEOUT_BTN"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal knowledge-test knowledge-test-timeout area-" + areaCssClass
    };

    // show or enqueue this modal
    queueModal(swalObject, callback);
};


const emptyStateSvg = `<svg width="138" height="126" viewBox="0 0 138 126" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_dd_886_1641)"><path d="M87.3932 90.6106L87.386 90.6147C87.3941 90.6098 87.3914 90.6106 87.379 90.6208C87.3848 90.6173 87.3922 90.612 87.4015 90.6063L87.3932 90.6106ZM56.4988 95.5087C56.4861 95.5038 56.484 95.5038 56.4924 95.5058L56.4802 95.5038C56.4874 95.5055 56.4935 95.5075 56.4988 95.5087ZM31.1755 77.1188L31.1712 77.1106L31.1661 77.1035C31.1711 77.1117 31.1709 77.1091 31.1612 77.0973L31.1755 77.1188ZM105.788 65.2894L105.786 65.3016C105.788 65.2941 105.79 65.2875 105.791 65.282C105.786 65.295 105.787 65.2976 105.788 65.2894ZM100.897 34.3958C100.892 34.3876 100.893 34.3897 100.903 34.4018L100.893 34.3876L100.897 34.3958ZM26.2726 46.2133C26.2767 46.2028 26.277 46.2018 26.275 46.2099L26.278 46.1976C26.2766 46.2036 26.2737 46.2086 26.2726 46.2133ZM75.5719 15.9924L75.5842 15.9954C75.5776 15.9938 75.5717 15.9914 75.5666 15.9902L75.5719 15.9924ZM112.311 48.4198C113.287 54.5864 109.516 59.607 107.735 62.1633L107.736 62.1641C107.132 63.035 106.644 63.741 106.26 64.3841C105.942 64.9178 105.826 65.1948 105.796 65.2715C105.793 65.325 105.777 65.5803 105.818 66.1311C105.844 66.4843 105.886 66.8644 105.939 67.2826L106.129 68.6624C106.581 71.829 107.715 78.0954 103.94 83.2914C100.637 87.8377 95.3936 89.0629 92.0231 89.6657L90.7036 89.8935C89.697 90.0687 88.8882 90.2119 88.2001 90.38C87.6568 90.5127 87.4198 90.6078 87.3737 90.6246C87.3137 90.6742 87.0871 90.8682 86.6751 91.3393C86.1825 91.9028 85.6634 92.5837 85.0199 93.4297C83.1403 95.9079 79.5311 101.051 73.362 102.028C67.1955 103.005 62.1741 99.2341 59.6177 97.453L58.422 96.6329C58.0562 96.3876 57.7193 96.1693 57.3977 95.9774C56.8627 95.6583 56.5853 95.5431 56.5094 95.513C56.4545 95.5108 56.1992 95.4943 55.6507 95.5353C54.9444 95.5881 54.1309 95.7018 53.1194 95.8463C49.9528 96.298 43.6853 97.4327 38.4895 93.6577C33.9435 90.3546 32.7189 85.1109 32.1161 81.7405L31.8874 80.4211C31.7122 79.4145 31.5699 78.6055 31.4018 77.9174C31.2678 77.3686 31.1695 77.1332 31.1542 77.0905C31.1025 77.0281 30.9086 76.801 30.4415 76.3926C29.878 75.9 29.198 75.3807 28.3521 74.7372C25.8737 72.8575 20.7307 69.2481 19.7536 63.0794C18.7758 56.9055 22.5568 51.8797 24.3353 49.3261C24.9363 48.4594 25.4211 47.756 25.8034 47.1152C26.1215 46.582 26.2363 46.304 26.2669 46.227C26.269 46.1729 26.2876 45.9176 26.2465 45.3681C26.1937 44.6617 26.079 43.8484 25.9346 42.8369L25.9326 42.8243L25.9314 42.8107C25.5026 39.6904 24.3476 33.4035 28.1231 28.207C31.8981 23.0111 38.2085 22.1537 41.3597 21.6048C42.3664 21.4296 43.1781 21.287 43.8673 21.1187C44.1991 21.0377 44.435 20.966 44.5943 20.9086C44.6403 20.8921 44.6728 20.8771 44.6947 20.868C44.7624 20.8117 44.9891 20.6165 45.3892 20.1588C45.8818 19.5954 46.4012 18.9153 47.0446 18.0695C48.9242 15.5912 52.5333 10.4481 58.7024 9.47097C64.8693 8.49432 69.8905 12.2652 72.4467 14.0462C73.3176 14.65 74.0236 15.1371 74.6667 15.5208C75.1986 15.8381 75.4761 15.9535 75.5538 15.9844C75.6066 15.9864 75.8619 16.0051 76.4137 15.9638C77.1201 15.911 77.9335 15.7964 78.9449 15.6519L80.2704 15.4618C83.6621 14.9936 89.0276 14.5378 93.5739 17.8406C98.7697 21.6156 99.6271 27.926 100.176 31.0772C100.351 32.0838 100.495 32.8936 100.663 33.5817C100.795 34.1225 100.89 34.3596 100.907 34.4072C100.956 34.4665 101.15 34.6937 101.622 35.1067C102.184 35.5983 102.863 36.1161 103.707 36.7579L104.747 37.5443C107.405 39.578 111.455 43.0175 112.311 48.4198Z" fill="white"/><path d="M100.694 40.7285C99.0717 39.4942 97.3942 38.2231 96.6426 36.9909C95.9492 35.863 95.6027 33.8664 95.2661 31.9325C94.6399 28.3374 93.934 24.263 90.6448 21.8732C87.3556 19.4834 83.2624 20.0712 79.6498 20.5866C77.7066 20.8641 75.7006 21.1515 74.4136 20.8406C73.0131 20.506 71.2822 19.304 69.6069 18.1423C66.6714 16.0978 63.3373 13.7832 59.4824 14.3938C55.6275 15.0044 53.1755 18.2353 51.0117 21.0875C49.7774 22.71 48.5063 24.3875 47.2741 25.139C46.1536 25.8313 44.1496 26.179 42.2157 26.5155C38.6207 27.1417 34.5462 27.8476 32.1564 31.1368C29.7667 34.4261 30.3729 38.5164 30.8698 42.1319C31.1473 44.0751 31.4347 46.0811 31.1238 47.368C30.7892 48.7685 29.5872 50.4994 28.4255 52.1747C26.381 55.1103 24.0664 58.4444 24.677 62.2993C25.2876 66.1542 28.5185 68.6062 31.3707 70.7699C32.9932 72.0043 34.6707 73.2753 35.4223 74.5075C36.1157 75.6354 36.4622 77.6321 36.7988 79.5659C37.4249 83.161 38.1308 87.2355 41.4201 89.6252C44.7093 92.015 48.8025 91.4273 52.4151 90.9119C54.3583 90.6344 56.3643 90.3469 57.6513 90.6579C59.0517 90.9924 60.7827 92.1945 62.458 93.3561C65.3935 95.4007 68.7276 97.7152 72.5825 97.1046C76.4374 96.4941 78.8894 93.2631 81.0531 90.4109C82.2875 88.7884 83.5585 87.111 84.7908 86.3594C85.9187 85.666 87.9153 85.3195 89.8491 84.9829C93.4442 84.3567 97.5187 83.6508 99.9085 80.3616C102.298 77.0724 101.71 72.9792 101.195 69.3666C100.918 67.4233 100.63 65.4174 100.941 64.1304C101.276 62.7299 102.478 60.999 103.639 59.3237C105.684 56.3882 107.998 53.0541 107.388 49.1992C106.777 45.3442 103.546 42.8923 100.694 40.7285ZM81.8196 46.3069L64.4169 70.2597C64.1861 70.5778 63.8948 70.8473 63.5597 71.0527C63.2247 71.2582 62.8524 71.3956 62.4642 71.4571C62.076 71.5185 61.6795 71.5029 61.2973 71.4111C60.9152 71.3192 60.5549 71.1529 60.237 70.9217L49.9716 63.4634C49.3295 62.9969 48.8991 62.2945 48.7749 61.5106C48.6507 60.7267 48.8431 59.9256 49.3096 59.2836C49.7761 58.6415 50.4785 58.211 51.2624 58.0869C52.0463 57.9627 52.8473 58.1551 53.4894 58.6215L61.3345 64.3246L76.9777 42.7891C77.2087 42.4712 77.5 42.2019 77.8351 41.9966C78.1702 41.7912 78.5424 41.6539 78.9305 41.5924C79.3187 41.531 79.7151 41.5465 80.0972 41.6383C80.4793 41.73 80.8396 41.8961 81.1576 42.1271C81.4755 42.3581 81.7448 42.6494 81.9501 42.9845C82.1554 43.3196 82.2928 43.6918 82.3542 44.0799C82.4157 44.468 82.4001 44.8645 82.3084 45.2466C82.2167 45.6287 82.0506 45.989 81.8196 46.3069Z" fill="#017632"/><path d="M19.8909 60.5612C21.2569 60.2676 22.6802 60.3994 23.9693 60.9383C25.178 61.4437 26.2152 62.2841 26.9586 63.3585L27.1032 63.5761L27.1111 63.5894L27.1187 63.6018L31.2611 70.203L38.9802 71.2672C40.3727 71.4556 41.6722 72.07 42.7024 73.0265C43.7231 73.9743 44.4272 75.2126 44.7229 76.5727L44.7249 76.5723C44.7273 76.5834 44.7287 76.5948 44.7311 76.6059C44.7335 76.6174 44.737 76.6286 44.7394 76.6401L44.7373 76.6395C45.0266 78.0012 44.8932 79.4191 44.3519 80.7028C43.8058 81.9977 42.8725 83.091 41.6807 83.8348L41.6819 83.8356L35.0789 87.9793L34.0155 95.6973L34.0138 95.7126L34.0119 95.727C33.8117 97.1099 33.1924 98.3993 32.2385 99.4203C31.2846 100.441 30.0409 101.146 28.675 101.44C27.3089 101.733 25.8856 101.601 24.5965 101.062C23.3073 100.523 22.214 99.6021 21.4634 98.4235L21.4557 98.4111L21.4469 98.398L17.3043 91.7958L9.58641 90.7324L9.55572 90.729C8.17282 90.5288 6.88441 89.9093 5.86339 88.9553C4.84247 88.0014 4.13763 86.7579 3.84409 85.3918C3.55061 84.0258 3.68228 82.6025 4.22126 81.3134C4.76027 80.0243 5.68078 78.931 6.85925 78.1805L6.87255 78.1726L6.88564 78.1638L13.4862 74.0226L14.5503 66.3035L14.553 66.288L14.5547 66.2726C14.7549 64.8898 15.3735 63.6014 16.3274 62.5805C17.2812 61.5595 18.5248 60.8548 19.8909 60.5612Z" fill="white"/><path d="M40.332 77.5513C40.4367 78.0208 40.3926 78.5111 40.2057 78.9543C40.0188 79.3975 39.6985 79.7714 39.2893 80.0241L30.909 85.2834L29.5577 95.0827C29.4891 95.5565 29.2772 95.9979 28.9504 96.3477C28.6236 96.6975 28.1975 96.9389 27.7295 97.0395C27.2615 97.14 26.7739 97.095 26.3322 96.9103C25.8906 96.7256 25.516 96.4102 25.2588 96.0064L20.001 87.6258L10.2003 86.2749C9.72648 86.2063 9.28505 85.9943 8.93525 85.6675C8.58545 85.3407 8.34405 84.9147 8.24348 84.4466C8.14291 83.9786 8.18798 83.491 8.37265 83.0494C8.55731 82.6077 8.87272 82.2331 9.2765 81.976L17.6571 76.7181L19.0081 66.9174C19.0767 66.4436 19.2886 66.0022 19.6154 65.6524C19.9422 65.3026 20.3683 65.0612 20.8363 64.9606C21.3043 64.86 21.7919 64.9051 22.2336 65.0898C22.6752 65.2744 23.0498 65.5899 23.307 65.9936L28.5662 74.374L38.3656 75.7252C38.8424 75.7875 39.2881 75.9969 39.6405 76.3242C39.9929 76.6514 40.2346 77.0804 40.332 77.5513Z" fill="#F2A91E"/><path d="M120.312 69.4166L120.63 69.4643C121.262 69.5778 121.873 69.7912 122.437 70.0967L122.716 70.2584L122.984 70.4318C123.601 70.8563 124.135 71.3932 124.557 72.0145L134.398 86.4998C135.237 87.7317 135.589 89.2303 135.39 90.708C135.189 92.1915 134.445 93.5483 133.302 94.5142L133.29 94.5257L107.389 116.24L107.387 116.237C106.752 116.769 106.017 117.167 105.223 117.402C104.429 117.638 103.594 117.706 102.772 117.605C101.95 117.504 101.157 117.236 100.444 116.816C99.7299 116.395 99.1092 115.832 98.6221 115.161L78.7434 87.8283C77.8631 86.6178 77.4638 85.1228 77.6225 83.6345C77.7814 82.1459 78.4889 80.7696 79.6051 79.7721L92.6538 68.0973C93.2936 67.5253 94.0491 67.0967 94.8677 66.8391L95.1769 66.7496C95.9018 66.5633 96.6562 66.5139 97.4012 66.6035L120.312 69.4166Z" fill="white"/><path d="M106.307 111.407L117.265 90.0576L129.919 91.6113L106.307 111.407Z" fill="#135EE2"/><path d="M100.827 110.717L82.7197 85.816L95.3716 87.3695L100.827 110.717Z" fill="#135EE2"/><path d="M103.819 109.037L98.8524 87.7968L113.78 89.6297L103.819 109.037Z" fill="#135EE2"/><path d="M96.0222 84.1301L83.0872 82.5419L95.582 71.3652C95.7564 71.2093 95.9628 71.0925 96.186 71.0223C96.409 70.9521 96.6446 70.9302 96.8767 70.958L106.359 72.1222L96.0222 84.1301Z" fill="#135EE2"/><path d="M113.464 86.2717L99.9521 84.6126L107.957 75.3136L113.464 86.2717Z" fill="#135EE2"/><path d="M119.766 73.7685C119.999 73.7976 120.224 73.8761 120.423 73.9982C120.622 74.1203 120.793 74.2837 120.924 74.4768L130.344 88.3443L117.394 86.7542L110.284 72.6042L119.766 73.7685Z" fill="#135EE2"/></g><defs><filter id="filter0_dd_886_1641" x="-1" y="0" width="140" height="126" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="2"/><feGaussianBlur stdDeviation="1"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_886_1641"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset/><feGaussianBlur stdDeviation="0.5"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/><feBlend mode="normal" in2="effect1_dropShadow_886_1641" result="effect2_dropShadow_886_1641"/><feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_886_1641" result="shape"/></filter></defs></svg>`;

const categoryCompletionModal = function(areaCssClass, keyPrefix) {
    let html = `<div class="inner-content">
                    <div class="category-complete-icon">${emptyStateSvg}</div>
                    <h1>${Vue.i18n.translate(keyPrefix + "_TITLE")}</h1>
                    <p class="category-complete-text">${Vue.i18n.translate(keyPrefix + "_TEXT")}</p>
                </div>`;

    queueModal({
        html,
        showCancelButton    : false,
        buttonsStyling      : false,
        confirmButtonText   : Vue.i18n.translate(keyPrefix + "_BACK_BUTTON"),
        confirmButtonClass  : "btn primary-btn",
        customClass         : "ifo-modal category-complete area-" + areaCssClass,
    });
};

const courseCompleted = function(areaCssClass) {
    categoryCompletionModal(areaCssClass, "CATEGORY_COMPLETE");
};

const areaCompleted = function(areaCssClass) {
    categoryCompletionModal(areaCssClass, "AREA_COMPLETE");
};


export default {
    acquirePermission,

    notify,
    startNotificationPolling,
    stopNotificationPolling,

    // modals
    newLevel,
    newAchievements,
    newAchievementsSingle,
    newAchievementsToast,
    courseCompleted,
    areaCompleted,

    // battles - modals
    battleUserChallenged,
    battleOpponentAccepted,
    battleOpponentDeclined,
    battleWon,
    battleLost,
    battleTie,

    // knowledge tests - modals
    knowledgeTestGeneric,
    knowledgeTestStart,
    knowledgeTestTimeout
};
